import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@rebass/forms';
import { useAuth } from 'context/AuthContext';
import { useToastHelper } from 'hooks/useToastHelper';
import { useUpdateAccountSettingsMutation } from 'api/useUpdateAccountSettingsMutation';
import services from 'services/services';
import auth from 'services/auth';

/**
 * Self-contained fatigue settings subsection.
 *
 * Reads current fatigue values from AuthContext and persists changes
 * via {@link useUpdateAccountSettingsMutation} with a 300 ms debounce
 * so rapid keystrokes don't fire a request per character.
 */
export function FatigueSettings() {
    const authContext = useAuth();
    const settings = authContext.account.settings;
    const currency = authContext.account.incentive_currency;
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const saveDelayRef = useRef(null);

    const [values, setValues] = useState({
        panelist_fatigue_email: settings.panelist_fatigue_email || '',
        panelist_fatigue_scheduling: settings.panelist_fatigue_scheduling || '',
        panelist_fatigue_incentive: settings.panelist_fatigue_incentive || ''
    });

    const { mutate } = useUpdateAccountSettingsMutation({
        onSuccess: () => {
            auth.checkAuth();
            addSuccessToast('Saved');
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error) || 'Something went wrong. Please try again later.';
            addErrorToast(errorText);
        }
    });

    const handleChange = e => {
        const { name, value } = e.target;
        setValues(prev => ({ ...prev, [name]: value }));
        authContext.account.settings[name] = value;

        if (saveDelayRef.current) clearTimeout(saveDelayRef.current);
        saveDelayRef.current = setTimeout(() => {
            mutate({ [name]: value });
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (saveDelayRef.current) clearTimeout(saveDelayRef.current);
        };
    }, []);

    return (
        <div id="panelist-fatigue" className="mb-10" role="group" aria-labelledby="fatigue-heading">
            <h3 id="fatigue-heading" className="fs-title-16 mb-1 flex items-center gap-2">
                Fatigue
                <a
                    href="https://help.dscout.com/hc/en-us/articles/37037325717652"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="help-question"
                >
                    ?
                </a>
            </h3>
            <p id="fatigue-description" className="form-section-information mb-4">
                Prevent adding a panelist to a new study if they&#39;ve participated recently.
            </p>
            <div className="flex flex-wrap border-l pl-4" style={{ fontSize: '14px' }}>
                <div className="form-label">
                    <label htmlFor="panelist_fatigue_email" className="fs-13 text-secondary">
                        Email Fatigue
                    </label>
                </div>
                <div className="form-data flex items-center">
                    <Input
                        id="panelist_fatigue_email"
                        type="number"
                        name="panelist_fatigue_email"
                        placeholder="0"
                        aria-describedby="fatigue-description"
                        value={values.panelist_fatigue_email}
                        style={{ width: '90px' }}
                        onChange={handleChange}
                    />
                    <span className="text-primary mt-1 ml-2">days</span>
                </div>
                <div className="form-label mt-4">
                    <label htmlFor="panelist_fatigue_scheduling" className="fs-13 text-secondary">
                        Scheduling Fatigue
                    </label>
                </div>
                <div className="form-data flex items-center">
                    <Input
                        id="panelist_fatigue_scheduling"
                        type="number"
                        name="panelist_fatigue_scheduling"
                        placeholder="0"
                        aria-describedby="fatigue-description"
                        value={values.panelist_fatigue_scheduling}
                        style={{ width: '90px' }}
                        onChange={handleChange}
                    />
                    <span className="text-primary mt-1 ml-2">days</span>
                </div>
                <div className="form-label mt-4">
                    <label htmlFor="panelist_fatigue_incentive" className="fs-13 text-secondary">
                        Annual Incentive Fatigue
                    </label>
                </div>
                <div className="form-data flex items-center">
                    <Input
                        id="panelist_fatigue_incentive"
                        type="number"
                        name="panelist_fatigue_incentive"
                        placeholder="0"
                        aria-describedby="fatigue-description"
                        value={values.panelist_fatigue_incentive}
                        style={{ width: '90px' }}
                        onChange={handleChange}
                    />
                    <span className="text-primary mt-1 ml-2">{currency}</span>
                </div>
            </div>
        </div>
    );
}
